import {
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import {
    eligibleProjectsResponseSchema,
    compressResponseSchema,
    EligibleProjectsResponse,
    CompressCommand,
    CompressResponse,
} from './locationTask.types';
import { createPacerApi } from '@Helpers/pacerApi';
import { parseMutationResponse, MutationResponse } from '../shared.types';
import { LOCATION_LINKS_QUERY_KEY } from '../locationDashboard/index';
import { UpdateLinkRequest } from './locationTask.types';

export type { UpdateLinkRequest } from './locationTask.types';
const ELIGIBLE_PROJECTS_QUERY_KEY = 'EligibleProjectsForCompression';

// ---- Update location task link ----

const updateLocationTaskLink = async (link: UpdateLinkRequest): Promise<MutationResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/update-link`, { json: link })
        .json();
    return parseMutationResponse(response);
};

export const useUpdateLocationTaskLink = (
    locationKey?: string,
    projectIds?: number[]
): UseMutationResult<MutationResponse, Error, UpdateLinkRequest> => {
    const queryClient = useQueryClient();
    const serializedIds = (projectIds ?? []).slice().sort((a, b) => a - b).join(',');
    return useMutation<MutationResponse, Error, UpdateLinkRequest>({
        mutationFn: updateLocationTaskLink,
        onSuccess: () => {
            if (locationKey) {
                queryClient.invalidateQueries({
                    queryKey: [LOCATION_LINKS_QUERY_KEY, locationKey, serializedIds],
                });
            }
        }
    })
}

// ---- Get Eligible Projects for Compression ----

const getEligibleProjectsForCompression = async (locationId: number): Promise<EligibleProjectsResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}location-task/compress/eligible-projects?locationId=${locationId}`)
        .json();
    return eligibleProjectsResponseSchema.parse(response);
};

export const useGetEligibleProjectsForCompression = (
    locationId: number,
    options?: { enabled?: boolean }
): UseQueryResult<EligibleProjectsResponse> => {
    return useQuery<EligibleProjectsResponse>({
        queryKey: [ELIGIBLE_PROJECTS_QUERY_KEY, locationId],
        queryFn: () => getEligibleProjectsForCompression(locationId),
        enabled: options?.enabled !== undefined ? options.enabled : !!locationId,
        gcTime: 0, // Always fetch fresh data — no caching between modal openings
    });
};

// ---- Compress Task Dates ----

const compressTaskDates = async (command: CompressCommand): Promise<CompressResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}location-task/compress`, { json: command })
        .json();
    return compressResponseSchema.parse(response);
};

export const useCompressTaskDates = (
    callback?: (results: CompressResponse) => void
): UseMutationResult<CompressResponse, Error, CompressCommand> => {
    return useMutation<CompressResponse, Error, CompressCommand>({
        mutationFn: compressTaskDates,
        onSuccess: (response) => {
            callback?.(response);
        },
    });
};
